import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

function loadSession() {
  try {
    const s = localStorage.getItem('sms_auth');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(loadSession); // { token, user }
  const [users, setUsersState] = useState([]);

  const currentUser = session?.user || null;

  const login = useCallback(async (email, password) => {
    try {
      const data = await api.login(email, password);
      const sess = { token: data.token, user: data.user };
      setSession(sess);
      localStorage.setItem('sms_auth', JSON.stringify(sess));
      return { success: true, user: data.user };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    localStorage.removeItem('sms_auth');
  }, []);

  // Load users list from API (called by UserManagement on mount)
  const loadUsers = useCallback(async () => {
    try {
      const list = await api.listUsers();
      setUsersState(list);
      return list;
    } catch { return []; }
  }, []);

  // Direct user mutations (called by UserManagement)
  const addUser = useCallback(async (userData) => {
    const created = await api.createUser(userData);
    await loadUsers();
    return created;
  }, [loadUsers]);

  const editUser = useCallback(async (id, userData) => {
    await api.updateUser(id, userData);
    await loadUsers();
  }, [loadUsers]);

  const deleteUser = useCallback(async (id) => {
    await api.deleteUser(id);
    setUsersState(prev => prev.filter(u => u.id !== id));
  }, []);

  const changePassword = useCallback(async (id, currentPassword, newPassword) => {
    await api.changePassword(id, currentPassword, newPassword);
  }, []);

  const refreshCurrentUser = useCallback((updatedFields) => {
    setSession(prev => {
      const sess = { ...prev, user: { ...prev?.user, ...updatedFields } };
      localStorage.setItem('sms_auth', JSON.stringify(sess));
      return sess;
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      currentUser, login, logout,
      users, loadUsers, addUser, editUser, deleteUser, changePassword,
      refreshCurrentUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
